<!--
  The snackbar stack displays alerts in the bottom right corner with position fixed.
  Alerts can be pushed to and displayed anywhere using the `hubUi` Vuex store
  (`hubUi/addAlert`); the store removes each alert again after its duration.

  The SnackbarStack and the alert objects can both use scopes, and the snackbar stack
  will show only the intersecting scopes. Alternatively, either the stack or the alert
  can use the wildcard scope to display all alerts/be displayed anywhere.

  Omission of scopes is equivalent to using the wildcard on both sides.
  I.e., if an alert has no scopes, it will be displayed anywhere.
  If a snackbar stack has no scopes, it will display all alerts.

  Scopes are an array of arbitrary strings of your choice.
-->
<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useHubUiStore } from '@/stores/hubUi';

interface HubUiAlert {
  id: string;
  duration: number;
  type?: 'info' | 'error' | 'success' | 'warning';
  message: string;
  to?: string | Record<string, unknown>;
  visible: boolean;
  scopes?: string[];
}

const props = withDefaults(
  defineProps<{
    scopes?: string[];
  }>(),
  { scopes: () => ['*'] },
);

const hubUiStore = useHubUiStore();
const { t } = useI18n();

/** All alerts that should be displayed for the scope of this component. */
const alerts = computed<HubUiAlert[]>(() =>
  hubUiStore.alertsInScope(props.scopes),
);

const typeClasses: Record<string, string> = {
  success: 'bg-green-600 text-white',
  error: 'bg-red-600 text-white',
  warning: 'bg-amber-500 text-white',
  info: 'bg-foreground text-background',
};

/**
 * Fade in on mount and fade back out right before the store removes the alert
 * (removal happens `duration + 300ms` after it was pushed).
 */
function snackbarStyle(alert: HubUiAlert) {
  return {
    animation: `snackbar-in 0.2s ease-out, snackbar-out 0.3s ease-in ${alert.duration}ms forwards`,
  };
}

/**
 * Provides a convenient way of pushing alerts to the snackbar stacks.
 * You can, of course, also just use the `hubUi/addAlert` mutation directly.
 * @example
 * <SnackbarStack ref="alertStack" :scopes="['test']">
 * alertStack.value.pushAlert({ message: 'Hi', scopes: ['test', 'other_scope'] });
 */
function pushAlert(alertObj: Partial<HubUiAlert>) {
  hubUiStore.addAlert(alertObj);
}

/** Adds an alert that will be using the same scope as this component instance. */
function pushAlertToSelf(alertObj: Partial<HubUiAlert>) {
  pushAlert({ ...alertObj, scopes: [...props.scopes] });
}

defineExpose({ pushAlert, pushAlertToSelf });
</script>

<template>
  <div
    class="pointer-events-none fixed bottom-4 right-4 z-[9999] flex flex-col items-end gap-2"
  >
    <template v-for="alert in alerts" :key="alert.id">
      <RouterLink v-if="alert.to" :to="alert.to" class="pointer-events-auto">
        <div
          class="min-w-56 max-w-96 rounded-md px-4 py-3 text-sm shadow-lg"
          :class="typeClasses[alert.type ?? 'info']"
          :style="snackbarStyle(alert)"
        >
          {{ t(alert.message) }}
        </div>
      </RouterLink>

      <div
        v-else
        class="pointer-events-auto min-w-56 max-w-96 rounded-md px-4 py-3 text-sm shadow-lg"
        :class="typeClasses[alert.type ?? 'info']"
        :style="snackbarStyle(alert)"
      >
        {{ t(alert.message) }}
      </div>
    </template>
  </div>
</template>

<style scoped>
@keyframes snackbar-in {
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes snackbar-out {
  to {
    opacity: 0;
    transform: translateY(8px);
  }
}
</style>
